import { useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Toast } from '@m/alchemy-ui/Toast';
import { CommunityContext } from '../../../context/Community';
import { type IdeaRow, MODERATION_STATUS } from '../IdeaCard.types';
import { getCommonCardFlags } from '../IdeaCard.helpers';
import {
  FlaggedIndicator,
  IdeaAuthor,
  IdeaBody,
  IdeaFooter,
  IdeaLabels,
  IdeaThumbnail,
  IdeaTitle,
  NextPhaseBadge,
  VoteActions
} from '../components';
import { useIdeaVoteState } from '../hooks/useIdeaVoteState';
import * as Styled from '../components/IdeaCard.styled';

interface FunnelIdeaCardProps {
  readonly idea: IdeaRow;
  readonly isOwnProfile: boolean;
}

export const FunnelIdeaCard = ({ idea, isOwnProfile }: FunnelIdeaCardProps) => {
  const community = useContext(CommunityContext);
  const challenge = idea.challenge;

  const { voteCount, score, toastMessages, portalTarget, portalTargetRef, handleVoteSuccess, handleVoteError } =
    useIdeaVoteState(idea.voteCount, idea.score);

  const { isAnonymous, hasAccess, canModerate, isFlagged, isArchived, isPrivate, isHidden } = getCommonCardFlags(
    idea,
    community.hideUnModerated === 1
  );

  const phases = challenge?.phases;
  const ideaEndPhase = idea.endPhase;

  // Use backend-provided effective phase directly
  const effectivePhase = idea.effectivePhase;

  const phaseAccess = effectivePhase?.access?.options;
  const isClosed = phaseAccess?.showClosedEnabled ?? challenge?.access?.options?.showClosedEnabled ?? false;
  const votingEnabled = phaseAccess?.voteOnIdeasEnabled ?? challenge?.access?.options?.voteOnIdeasEnabled ?? false;

  const phaseVoting = effectivePhase?.voting;
  const effectiveVotingType = phaseVoting?.type ?? challenge?.voting?.type ?? null;
  const isBlindVoting = phaseVoting?.isBlind ?? challenge?.voting?.isBlind ?? false;
  const votingOptions = phaseVoting?.options ?? challenge?.voting?.options ?? null;
  const walletConfig = phaseVoting?.wallet ?? challenge?.voting?.wallet ?? null;
  const walletEmpty = walletConfig?.isEnabled && idea.viewerWalletAllowance === 0 && idea.viewerVoteDirection === null;

  // Check if the effective phase has expired
  const isEffectivePhaseExpired = useMemo(() => {
    if (!effectivePhase?.startDate || !phases?.length) return false;
    const now = new Date();
    const effectiveStart = new Date(effectivePhase.startDate).getTime();
    return phases.some(
      (p) => p.startDate && new Date(p.startDate).getTime() > effectiveStart && new Date(p.startDate) <= now
    );
  }, [effectivePhase, phases]);

  const hideVoteButtons = isArchived || isClosed || isEffectivePhaseExpired;
  const isVotingDisabled =
    isOwnProfile ||
    hideVoteButtons ||
    walletEmpty ||
    !votingEnabled ||
    idea.moderationStatus === MODERATION_STATUS.NOT_APPROVED;

  // "In next phase" label check
  const isInNextPhase = useMemo(() => {
    if (!ideaEndPhase || !phases) return false;
    const now = new Date();
    const sortedPhases = [...phases]
      .filter((p) => p.startDate)
      .sort((a, b) => new Date(a.startDate!).getTime() - new Date(b.startDate!).getTime());
    const nextPhase = sortedPhases.find((p) => new Date(p.startDate!) > now);
    return nextPhase ? String(nextPhase.id) === String(ideaEndPhase) : false;
  }, [ideaEndPhase, phases]);

  return (
    <>
      <Styled.CardWrapper $hasTopBadge={isInNextPhase}>
        {isFlagged && (isOwnProfile || canModerate) && <FlaggedIndicator />}

        {isInNextPhase && <NextPhaseBadge />}

        {idea.imageFile && (
          <IdeaThumbnail
            imageFile={idea.imageFile}
            ideaId={idea.id}
            hasAccess={hasAccess}
            hasTopBadge={isInNextPhase}
          />
        )}

        <Styled.ContentWrapper $hasTopBadge={isInNextPhase}>
          <IdeaLabels
            moderationStatus={idea.moderationStatus}
            statusLabels={idea.statusLabels}
            isHidden={isHidden}
            isPrivate={isPrivate}
          />

          <IdeaTitle ideaId={idea.id} title={idea.title} hasAccess={hasAccess} />
          <IdeaBody ideaId={idea.id} text={idea.text} hasAccess={hasAccess} />

          <IdeaFooter
            voteCount={voteCount}
            score={score}
            commentCount={idea.commentCount ?? 0}
            votingType={effectiveVotingType}
            isBlindVoting={isBlindVoting}
            canModerate={canModerate}
            maxStars={votingOptions?.maxStars ?? undefined}
            suffix={
              <>
                {hideVoteButtons && (
                  <Styled.VotingClosed>
                    <FormattedMessage id="votingClosed" defaultMessage="Voting closed" />
                  </Styled.VotingClosed>
                )}
                {!isOwnProfile && hasAccess && !hideVoteButtons && (
                  <VoteActions
                    ideaId={idea.id}
                    viewerVoteDirection={idea.viewerVoteDirection}
                    votingType={effectiveVotingType}
                    votingOptions={votingOptions}
                    disabled={isVotingDisabled}
                    onVoteSuccess={handleVoteSuccess}
                    onVoteError={handleVoteError}
                  />
                )}
              </>
            }
          />
        </Styled.ContentWrapper>

        <IdeaAuthor
          ideaId={idea.id}
          user={idea.user}
          isAnonymous={isAnonymous}
          createdAt={idea.createdAt}
          hasTopBadge={isInNextPhase}
        />
      </Styled.CardWrapper>
      <div ref={portalTargetRef} style={{ position: 'relative' }}>
        {/* @ts-expect-error - Toast portalTarget typing mismatch */}
        <Toast messages={toastMessages} portalTarget={portalTarget} />
      </div>
    </>
  );
};
